import {
  Badge,
  Col,
  List,
  Modal,
  Radio,
  RadioChangeEvent,
  Row,
  Space,
  Spin,
  theme,
  Tooltip,
} from "antd";
import { useEffect, useState } from "react";
import {
  usePCMsgManagerControllerPostGetClassList,
  usePCMsgManagerControllerPostGetClassMsg,
  usePCMsgManagerControllerPostReachMsg,
} from "@/openapi/baob/pc.MsgManager.hook";
import NoticeItem from "@/components/HeaderNotice/NoticeItem";
import {
  NoticeItemType,
  NoticeTabType,
  ReadStatus,
} from "@/components/HeaderNotice/types";
import YsIcon from "@/components/Iconfont";
import { sendLog } from "@/utils/tracker";

const HeaderNotice = () => {
  const { token: globalToken } = theme.useToken();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("");
  const [activeType, setActiveType] = useState(ReadStatus.ALL);
  const [noticeList, setNoticeList] = useState<NoticeItemType[]>([]);

  const {
    isLoading: loading,
    data: noticeData,
    refetch: getNoticeClassData,
  } = usePCMsgManagerControllerPostGetClassMsg({
    params: {},
    query: {
      refetchInterval: () => {
        return !open ? 2 * 60 * 1000 : false;
      },
    },
  });
  const {
    isLoading: loadingList,
    data: noticeTabData,
    refetch: getNoticeTabData,
  } = usePCMsgManagerControllerPostGetClassList({
    params: {
      class_id: activeTab,
    },
    query: {
      enabled: !loading && !!activeTab,
    },
  });
  const { mutate: readNotice } = usePCMsgManagerControllerPostReachMsg({
    mutation: {
      onSuccess: () => {
        getNoticeClassData();
        getNoticeTabData();
      },
    },
  });

  const optionsType = [
    {
      label: "全部消息",
      value: ReadStatus.ALL,
    },
    {
      label: `仅显示未读(${noticeTabData?.unread_num || 0})`,
      value: ReadStatus.UNREAD,
    },
    {
      label: "仅显示已读",
      value: ReadStatus.READ,
    },
  ];

  const handleViewNoticeModal = () => {
    setOpen(true);
    sendLog("pcMainFloatingFrame", "主悬浮框", {
      click_source: "消息",
    });
    sendLog("pcNotify", "消息通知", {
      click_source: "tab",
      tab_name: noticeData?.[0]?.class_name,
    });
  };

  const handleCloseNoticeModal = () => {
    setOpen(false);
  };

  const handleTypeChange = (event: RadioChangeEvent) => {
    setActiveType(event.target.value);
  };

  const handleRead = (notice: NoticeItemType) => {
    readNotice({ id: notice.mps_id });
    sendLog("pcNotify", "消息通知", {
      click_source: "我已知悉",
      tab_name:
        noticeData?.find((item) => item.class_id === activeTab)?.class_name ||
        "",
      message_status:
        activeType === ReadStatus.READ
          ? "仅显示已读"
          : activeType === ReadStatus.UNREAD
            ? "仅显示未读"
            : "全部消息",
    });
  };

  const handleJump = (notice: NoticeItemType) => {
    sendLog("pcNotify", "消息通知", {
      click_source: "立即查看",
      tab_name:
        noticeData?.find((item) => item.class_id === activeTab)?.class_name ||
        "",
      message_status:
        activeType === ReadStatus.READ
          ? "仅显示已读"
          : activeType === ReadStatus.UNREAD
            ? "仅显示未读"
            : "全部消息",
    });
    window.open(notice.jump_link, "_blank");
  };

  const handleClickNoticeTab = (item: NoticeTabType) => {
    setActiveTab(item.class_id);
    sendLog("pcNotify", "消息通知", {
      click_source: "tab",
      tab_name: item.class_name,
    });
  };

  useEffect(() => {
    // 如果当前没有选中tab，则切换到第一个tab
    if (!activeTab) {
      setActiveTab(noticeData?.[0]?.class_id || "");
    }
  }, [activeTab, noticeData]);

  useEffect(() => {
    if (activeType === ReadStatus.ALL) {
      setNoticeList(noticeTabData?.list || []);
      return;
    }
    const filterList =
      noticeTabData?.list?.filter((item) => item.is_reach === activeType) || [];
    setNoticeList(filterList);
  }, [noticeTabData, activeType]);

  return (
    <>
      <Tooltip title="消息通知" color={globalToken.colorPrimary}>
        <YsIcon
          type="icon-tongzhi"
          className="p-2 cursor-pointer hover:bg-[rgba(0,0,0,0.025)] text-2xl"
          onClick={handleViewNoticeModal}
        />
      </Tooltip>
      <Modal
        open={open}
        title="消息通知"
        styles={{
          header: {
            marginBottom: 0,
            padding: "20px 24px",
            borderBottom: "1px solid #eee",
          },
          body: { borderRadius: "8px", overflow: "hidden" },
          content: { padding: "0 0 0 0" },
        }}
        width={1000}
        footer={false}
        maskClosable={false}
        onCancel={handleCloseNoticeModal}
      >
        <Spin spinning={loading || loadingList}>
          <Row className="h-[560px]" wrap={false}>
            <Col flex="200px">
              <div className="bg-white p-2 h-full overflow-y-auto">
                {noticeData?.map((item) => (
                  <div
                    key={item.class_id}
                    className="w-full box-border h-10 pl-10 mb-2 flex flex-row items-center rounded cursor-pointer hover:bg-[rgba(255,199,18,0.1)]"
                    style={{
                      background:
                        activeTab === item.class_id
                          ? "rgba(255,199,18,0.1)"
                          : "",
                    }}
                    onClick={() => handleClickNoticeTab(item)}
                  >
                    <span
                      className="text-sm font-medium text-[#666666]"
                      style={{
                        color:
                          activeTab === item.class_id
                            ? globalToken.colorPrimary
                            : "",
                      }}
                    >
                      {item.class_name}
                    </span>
                    {!!item.unread_num && (
                      <Badge
                        count={item.unread_num}
                        showZero
                        color="#F65B5B"
                        className="ml-1"
                        size="small"
                        style={{ borderRadius: "5px", fontSize: "10px" }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </Col>
            <Col flex={1}>
              <div className="w-full h-full bg-[#FAFAFA]">
                <div className="px-4 py-2 flex flex-row items-center justify-between">
                  <Radio.Group value={activeType} onChange={handleTypeChange}>
                    <Space>
                      {optionsType.map((item) => (
                        <Radio key={item.value} value={item.value}>
                          {item.label}
                        </Radio>
                      ))}
                    </Space>
                  </Radio.Group>
                  <div>共{noticeTabData?.total || 0}条</div>
                </div>
                <div className="px-4 h-[520px]">
                  <List
                    dataSource={noticeList}
                    renderItem={(item) => (
                      <List.Item
                        key={item.mps_id}
                        className="bg-white rounded mb-2 hover:shadow-sm"
                      >
                        <NoticeItem
                          data={item}
                          onJump={handleJump}
                          onRead={handleRead}
                        />
                      </List.Item>
                    )}
                    className="h-full overflow-y-auto"
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Spin>
      </Modal>
    </>
  );
};

export default HeaderNotice;
